import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PerformanceMetrics = () => {
  const [activeTab, setActiveTab] = useState('emissions');

  const emissionsData = [
    { month: 'Sep', emissions: 2450, target: 2200, cost: 185000 },
    { month: 'Oct', emissions: 2380, target: 2200, cost: 179400 },
    { month: 'Nov', emissions: 2520, target: 2200, cost: 190800 },
    { month: 'Dec', emissions: 2290, target: 2200, cost: 173100 },
    { month: 'Jan', emissions: 2150, target: 2200, cost: 162750 },
    { month: 'Feb', emissions: 2340, target: 2200, cost: 177300 }
  ];

  const euaForecastData = [
    { period: 'Q4 2024', demand: 12450, purchased: 8200, price: 75.50 },
    { period: 'Q1 2025', demand: 11800, purchased: 3200, price: 78.20 },
    { period: 'Q2 2025', demand: 13200, purchased: 1500, price: 82.10 },
    { period: 'Q3 2025', demand: 12900, purchased: 0, price: 85.30 }
  ];

  const fleetEfficiencyData = [
    { name: 'A Rating', value: 8, color: '#38A169' },
    { name: 'B Rating', value: 12, color: '#3182CE' },
    { name: 'C Rating', value: 3, color: '#D69E2E' },
    { name: 'D Rating', value: 1, color: '#E53E3E' }
  ];

  const complianceData = [
    { category: 'EU ETS', compliant: 23, total: 24, percentage: 95.8 },
    { category: 'FuelEU', compliant: 22, total: 24, percentage: 91.7 },
    { category: 'IMO CII', compliant: 20, total: 24, percentage: 83.3 },
    { category: 'MRV', compliant: 24, total: 24, percentage: 100 }
  ];

  const tabs = [
    { id: 'emissions', label: 'Emissions Trend', icon: 'TrendingUp' },
    { id: 'eua', label: 'EUA Forecast', icon: 'Calculator' },
    { id: 'efficiency', label: 'Fleet Efficiency', icon: 'PieChart' },
    { id: 'compliance', label: 'Compliance Status', icon: 'Shield' }
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US')?.format(value);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-maritime-md">
          <p className="text-sm font-medium text-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry?.color }}>
              {entry?.name}: {entry?.name?.includes('cost') || entry?.name?.includes('price') 
                ? formatCurrency(entry?.value) 
                : formatNumber(entry?.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const renderEmissionsChart = () => (
    <div className="space-y-4">
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={emissionsData}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="month" 
              stroke="var(--color-muted-foreground)"
              fontSize={12}
            />
            <YAxis 
              stroke="var(--color-muted-foreground)"
              fontSize={12}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="emissions" fill="var(--color-primary)" radius={[4, 4, 0, 0]} />
            <Bar dataKey="target" fill="var(--color-muted)" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      <div className="grid grid-cols-3 gap-4 text-center">
        <div className="p-3 bg-muted/50 rounded-lg">
          <div className="text-2xl font-bold text-foreground">14.1K</div>
          <div className="text-sm text-muted-foreground">Total CO₂e (tons)</div>
        </div>
        <div className="p-3 bg-muted/50 rounded-lg">
          <div className="text-2xl font-bold text-success">-8.2%</div>
          <div className="text-sm text-muted-foreground">vs Last Period</div>
        </div>
        <div className="p-3 bg-muted/50 rounded-lg">
          <div className="text-2xl font-bold text-foreground">€1.07M</div>
          <div className="text-sm text-muted-foreground">Total Cost</div>
        </div>
      </div>
    </div>
  );

  const renderEUAChart = () => (
    <div className="space-y-4">
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={euaForecastData}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="period" 
              stroke="var(--color-muted-foreground)"
              fontSize={12}
            />
            <YAxis 
              stroke="var(--color-muted-foreground)"
              fontSize={12}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line 
              type="monotone" 
              dataKey="demand" 
              stroke="var(--color-primary)" 
              strokeWidth={3}
              dot={{ fill: 'var(--color-primary)', strokeWidth: 2, r: 6 }}
            />
            <Line 
              type="monotone" 
              dataKey="purchased" 
              stroke="var(--color-success)" 
              strokeWidth={3}
              dot={{ fill: 'var(--color-success)', strokeWidth: 2, r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      
      <div className="grid grid-cols-3 gap-4 text-center">
        <div className="p-3 bg-muted/50 rounded-lg">
          <div className="text-2xl font-bold text-foreground">50.4K</div>
          <div className="text-sm text-muted-foreground">Total Demand</div>
        </div>
        <div className="p-3 bg-muted/50 rounded-lg">
          <div className="text-2xl font-bold text-success">12.9K</div>
          <div className="text-sm text-muted-foreground">Purchased</div>
        </div>
        <div className="p-3 bg-muted/50 rounded-lg">
          <div className="text-2xl font-bold text-warning">37.5K</div>
          <div className="text-sm text-muted-foreground">Remaining</div>
        </div>
      </div>
    </div>
  );

  const renderEfficiencyChart = () => (
    <div className="space-y-4">
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={fleetEfficiencyData}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={120}
              paddingAngle={5}
              dataKey="value"
            >
              {fleetEfficiencyData?.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry?.color} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        {fleetEfficiencyData?.map((item) => (
          <div key={item?.name} className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg">
            <div 
              className="w-4 h-4 rounded-full"
              style={{ backgroundColor: item?.color }}
            />
            <div className="flex-1">
              <div className="text-sm font-medium text-foreground">{item?.name}</div>
              <div className="text-xs text-muted-foreground">{item?.value} vessels</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderComplianceChart = () => (
    <div className="space-y-4">
      {complianceData?.map((item) => (
        <div key={item?.category} className="p-4 bg-muted/50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">{item?.category}</span>
            <span className="text-sm font-bold text-foreground">{item?.percentage}%</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all duration-300 ${
                item?.percentage >= 95 ? 'bg-success' :
                item?.percentage >= 85 ? 'bg-warning' : 'bg-error'
              }`}
              style={{ width: `${item?.percentage}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>{item?.compliant} compliant</span>
            <span>{item?.total} total vessels</span>
          </div>
        </div>
      ))}
    </div>
  );

  const renderActiveChart = () => {
    switch (activeTab) {
      case 'emissions': return renderEmissionsChart();
      case 'eua': return renderEUAChart();
      case 'efficiency': return renderEfficiencyChart();
      case 'compliance': return renderComplianceChart();
      default: return renderEmissionsChart();
    }
  };

  return (
    <div className="maritime-card">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Performance Metrics</h2>
            <p className="text-sm text-muted-foreground">
              Fleet-wide analytics and forecasting
            </p>
          </div>
          
          <Button variant="outline" size="sm" iconName="Download" iconPosition="left">
            Export Data
          </Button>
        </div>

        <div className="flex space-x-1">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-maritime ${
                activeTab === tab?.id
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <Icon name={tab?.icon} size={16} strokeWidth={2} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="p-6">
        {renderActiveChart()}
      </div>
    </div>
  );
};

export default PerformanceMetrics;